import React, { useState } from 'react';
import { teams, GAME_INFO, ITEMS_INFO } from '../data/teams';
import { getSpriteUrl, getItemSpriteUrl, getTypeIconUrl, handleItemIconError } from '../data/sprites';
import { getBaseStats, canMegaEvolve, getMegaStats, getMegaTypes, getMegaStones, getMegaStatsByStone, getMegaAbility } from '../data/baseStats';
import { getMegaSpriteUrl } from '../data/sprites';
import MOVE_TYPES from '../data/moves';
import ABILITIES from '../data/abilities';
import MOVE_DATA from '../data/moveData';
import { useTeams } from '../auth/useTeams';

const TYPE_COLORS = {
  Normal: '#A8A878', Fire: '#F08030', Water: '#6890F0', Electric: '#F8D030',
  Grass: '#78C850', Ice: '#98D8D8', Fighting: '#C03028', Poison: '#A040A0',
  Ground: '#E0C068', Flying: '#A890F0', Psychic: '#F85888', Bug: '#A8B820',
  Rock: '#B8A038', Ghost: '#705898', Dragon: '#7038F8', Dark: '#705848',
  Steel: '#B8B8D0', Fairy: '#EE99AC',
};

function TypeBadge({ type }) {
  return (
    <span className="type-badge" style={{ backgroundColor: TYPE_COLORS[type] || '#888' }}>
      {type}
    </span>
  );
}

const STATUS_MOVE_TYPES = {
  'Swords Dance': 'Normal', 'Nasty Plot': 'Dark', 'Dragon Dance': 'Dragon',
  'Calm Mind': 'Psychic', 'Quiver Dance': 'Bug', 'Shell Smash': 'Normal',
  'Protect': 'Normal', 'Roost': 'Flying', 'Recover': 'Normal', 'Slack Off': 'Normal',
  'Stealth Rock': 'Rock', 'Will-O-Wisp': 'Fire', 'Thunder Wave': 'Electric',
  'Toxic': 'Poison', 'Taunt': 'Dark', 'Whirlwind': 'Normal', 'Haze': 'Ice',
  'U-turn': 'Bug', 'Volt Switch': 'Electric', 'Parting Shot': 'Dark',
  'Trick Room': 'Psychic', 'Tailwind': 'Flying', 'Helping Hand': 'Normal',
  'Fake Out': 'Normal', 'Follow Me': 'Normal', 'Rage Powder': 'Bug',
  'Encore': 'Normal', 'Knock Off': 'Dark', 'Trick': 'Psychic',
  'Leech Seed': 'Grass', 'Substitute': 'Normal', 'Bulk Up': 'Fighting',
  'Iron Defense': 'Steel', 'Agility': 'Psychic',
};

const TYPE_TEXT_COLORS = {
  Normal: '#6d6d4e', Fire: '#c23616', Water: '#2e6cb5', Electric: '#a68a00',
  Grass: '#4a7a2e', Ice: '#4a8f8f', Fighting: '#8a2020', Poison: '#723090',
  Ground: '#8a7530', Flying: '#6a58c0', Psychic: '#c93868', Bug: '#6d7a10',
  Rock: '#8a7a28', Ghost: '#4e3870', Dragon: '#5028c8', Dark: '#504038',
  Steel: '#7a7a98', Fairy: '#c46878',
};

function moveStyle(moveName) {
  const type = MOVE_TYPES[moveName] || STATUS_MOVE_TYPES[moveName];
  if (!type) return {};
  const bg = TYPE_COLORS[type] || '#888';
  const text = TYPE_TEXT_COLORS[type] || bg;
  return { background: bg + '18', color: text, borderColor: bg + '30' };
}

function TeamBrowser({ onSelectTeam, onUseInAdvisor }) {
  const [archetypeFilter, setArchetypeFilter] = useState('all');
  const [formatFilter, setFormatFilter] = useState('doubles');
  const [expandedId, setExpandedId] = useState(null);
  const [megaState, setMegaState] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const { saveTeam: saveTeamToCloud } = useTeams();

  const toggleMega = (name, stone) => {
    setMegaState(prev => prev[name] === stone ? { ...prev, [name]: null } : { ...prev, [name]: stone });
  };

  const copyTeamToMyTeams = async (team) => {
    try {
      await saveTeamToCloud({
        id: Date.now().toString(),
        name: team.name,
        pokemon: team.pokemon.map(p => ({ ...p, moves: [...(p.moves || [])] })),
        // Carry over the structured advisorStrategy as the saved team's
        // `strategy` so copied presets keep their bring rates / triggers /
        // lead rules / scripts / 1v1s and the advisor can use them.
        ...(team.advisorStrategy ? { strategy: team.advisorStrategy } : {}),
      });
      setCopiedId(team.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy team:', err);
    }
  };

  const handleUseInAdvisor = (team) => {
    if (!onUseInAdvisor) return;
    // Pass the preset object verbatim so its advisorStrategy reaches the
    // advisor without needing to copy to MyTeams first.
    onUseInAdvisor(team);
  };

  const formatTeams = teams.filter(t => (t.format || 'doubles') === formatFilter);
  const archetypes = [...new Set(formatTeams.map(t => t.archetype))];

  const filtered = formatTeams.filter(t => {
    if (archetypeFilter !== 'all' && t.archetype !== archetypeFilter) return false;
    return true;
  });

  return (
    <div className="team-browser">
      <div className="browser-header">
        <h2>Competitive Teams</h2>
        <p className="subtitle">
          {GAME_INFO.format} &middot; Mega Evolution allowed &middot; {GAME_INFO.activePeriod}
        </p>
        <div className="browser-controls">
          <div className="format-toggle-bar">
            <button className={`format-toggle-opt${formatFilter === 'doubles' ? ' active' : ''}`} onClick={() => { setFormatFilter('doubles'); setArchetypeFilter('all'); }}>
              Doubles (VGC)
            </button>
            <button className={`format-toggle-opt${formatFilter === 'singles' ? ' active' : ''}`} onClick={() => { setFormatFilter('singles'); setArchetypeFilter('all'); }}>
              Singles
            </button>
          </div>
          <select value={archetypeFilter} onChange={e => setArchetypeFilter(e.target.value)}>
            <option value="all">All Archetypes</option>
            {archetypes.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      <div className="team-grid">
        {filtered.map(team => (
          <div key={team.id} className={`team-card${expandedId === team.id ? ' team-card-expanded' : ''}`} onClick={() => {
            if (expandedId !== team.id) setExpandedId(team.id);
          }}>
            <div className="team-card-header">
              <span className="format-tag champions">{team.archetype}</span>
              <span className={`difficulty-tag ${team.difficulty.toLowerCase()}`}>
                {team.difficulty}
              </span>
            </div>
            <h3>{team.name}</h3>
            <p className="team-desc">{team.description}</p>
            {expandedId !== team.id && (
              <div className="team-pokemon-preview">
                {team.pokemon.map(p => (
                  <div key={p.name} className="pokemon-mini">
                    <img src={getSpriteUrl(p.name)} alt={p.name} className="pokemon-mini-sprite" onError={e => { e.target.style.display = 'none'; }} />
                    <span className="pokemon-mini-name">{p.name}</span>
                    <div className="pokemon-mini-types">
                      {p.types.map(t => <TypeBadge key={t} type={t} />)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {expandedId === team.id && (
              <div className="team-card-detail" onClick={e => e.stopPropagation()}>
                <div className="team-card-detail-actions">
                  {onUseInAdvisor && (
                    <button className="action-btn use" onClick={() => handleUseInAdvisor(team)}>
                      Use in Battle Advisor →
                    </button>
                  )}
                  <button className="action-btn use" onClick={() => copyTeamToMyTeams(team)}>
                    {copiedId === team.id ? '✓ Copied!' : 'Copy to My Teams'}
                  </button>
                  <button className="action-btn edit" onClick={() => setExpandedId(null)}>Close</button>
                </div>

                <div className="saved-team-grid">
                  {team.pokemon.map((p, idx) => {
                    const megaStones = getMegaStones(p.name);
                    const hasMega = megaStones.length > 0;
                    const activeMegaStone = megaState[p.name] || null;
                    const isMega = !!activeMegaStone;

                    const baseStats = p.baseStats || getBaseStats(p.name);
                    const displayStats = isMega ? (getMegaStatsByStone(p.name, activeMegaStone) || baseStats) : baseStats;
                    const megaTypes = isMega ? getMegaTypes(p.name, activeMegaStone) : null;
                    const displayTypes = megaTypes || p.types;
                    const displayName = isMega ? `Mega ${p.name}${megaStones.length > 1 ? (activeMegaStone.includes('X') ? ' X' : ' Y') : ''}` : p.name;
                    const displayAbility = isMega ? (getMegaAbility(p.name, activeMegaStone) || p.ability) : p.ability;

                    // Nature arrows
                    const natureMap = {
                      Adamant: { up: 'atk', down: 'spa' }, Bold: { up: 'def', down: 'atk' },
                      Brave: { up: 'atk', down: 'spe' }, Calm: { up: 'spd', down: 'atk' },
                      Careful: { up: 'spd', down: 'spa' }, Gentle: { up: 'spd', down: 'def' },
                      Hasty: { up: 'spe', down: 'def' }, Impish: { up: 'def', down: 'spa' },
                      Jolly: { up: 'spe', down: 'spa' }, Lax: { up: 'def', down: 'spd' },
                      Lonely: { up: 'atk', down: 'def' }, Mild: { up: 'spa', down: 'def' },
                      Modest: { up: 'spa', down: 'atk' }, Naive: { up: 'spe', down: 'spd' },
                      Naughty: { up: 'atk', down: 'spd' }, Quiet: { up: 'spa', down: 'spe' },
                      Rash: { up: 'spa', down: 'spd' }, Relaxed: { up: 'def', down: 'spe' },
                      Sassy: { up: 'spd', down: 'spe' }, Timid: { up: 'spe', down: 'atk' },
                    };
                    const nm = natureMap[p.nature] || {};

                    // EVs: convert from old 252-scale to SP (1 SP = 8 EVs)
                    const sp = p.statPoints || {};
                    const evs = p.evs || {};

                    const SP_COLORS = { hp: '#ef4444', atk: '#f59e0b', def: '#f97316', spa: '#3b82f6', spd: '#22c55e', spe: '#ec4899' };
                    const statList = [
                      { key: 'hp', label: 'HP', color: SP_COLORS.hp },
                      { key: 'atk', label: 'Atk', color: SP_COLORS.atk },
                      { key: 'def', label: 'Def', color: SP_COLORS.def },
                      { key: 'spa', label: 'SpA', color: SP_COLORS.spa },
                      { key: 'spd', label: 'SpD', color: SP_COLORS.spd },
                      { key: 'spe', label: 'Spe', color: SP_COLORS.spe },
                    ];
                    return (
                      <div key={p.name} className={`saved-pokemon-card${isMega ? ' mega-active' : ''}`} style={{ animationDelay: `${idx * 0.05}s` }}>
                        <div className="saved-pokemon-card-top">
                          <img src={isMega ? getMegaSpriteUrl(p.name, activeMegaStone) : getSpriteUrl(p.name)} alt="" className="saved-pokemon-card-sprite" onError={e => { e.target.src = getSpriteUrl(p.name); e.target.onerror = () => { e.target.style.display = 'none'; }; }} />
                          <div className="saved-pokemon-card-identity">
                            <span className="saved-pokemon-card-name">{displayName}</span>
                            <div className="saved-pokemon-card-types">
                              {displayTypes.map(t => <TypeBadge key={t} type={t} />)}
                            </div>
                          </div>
                        </div>
                        <div className="mega-toggle-row">
                          {hasMega && megaStones.map(({ stone }) => {
                            const label = megaStones.length > 1 ? (stone.includes('X') ? 'Mega X' : 'Mega Y') : 'Mega';
                            const isActive = activeMegaStone === stone;
                            return (
                              <button key={stone} className={`mega-toggle-btn${isActive ? ' active' : ''}`} onClick={() => toggleMega(p.name, stone)}>
                                {isActive ? '✦ ' : ''}{label}
                              </button>
                            );
                          })}
                          {!hasMega && <span className="mega-toggle-spacer">&nbsp;</span>}
                        </div>
                        <div className="saved-pokemon-card-meta">
                          {displayAbility && <span className="saved-pokemon-meta-item ability" data-tooltip={ABILITIES[displayAbility] || ''}>{displayAbility}</span>}
                          {p.item && <span className="saved-pokemon-meta-item item" data-tooltip={ITEMS_INFO[p.item] || ''}><img src={getItemSpriteUrl(p.item)} alt="" className="item-sprite" onError={e => handleItemIconError(e, p.item)} />{p.item}</span>}
                          {p.nature && <span className="saved-pokemon-meta-item nature">{p.nature}</span>}
                        </div>
                        {displayStats && (
                          <div className="pkmn-stats">
                            {statList.map(({ key, label, color }) => {
                              const val = displayStats[key] || 0;
                              const baseVal = baseStats ? (baseStats[key] || 0) : val;
                              const diff = isMega ? val - baseVal : 0;
                              const spVal = sp[key] || (evs[key] ? Math.round(evs[key] / 8) : 0);
                              const isUp = nm.up === key;
                              const isDown = nm.down === key;
                              return (
                                <div key={key} className="pkmn-stat">
                                  <span className={`pkmn-stat-label${isUp ? ' pkmn-stat-up' : ''}${isDown ? ' pkmn-stat-down' : ''}`}>
                                    {isUp && <span className="pkmn-nature-arrow up">▲</span>}
                                    {isDown && <span className="pkmn-nature-arrow down">▼</span>}
                                    {label}
                                  </span>
                                  <span className="pkmn-stat-base">{val}{diff !== 0 && <span className={`pkmn-stat-diff${diff > 0 ? ' pos' : ' neg'}`}>{diff > 0 ? '+' : ''}{diff}</span>}</span>
                                  <div className="pkmn-stat-track">
                                    <div className="pkmn-stat-fill" style={{ width: `${Math.min(val / 200 * 100, 100)}%`, background: color }} />
                                  </div>
                                  <span className="pkmn-stat-ev">{spVal > 0 ? spVal : ''}</span>
                                </div>
                              );
                            })}
                            <div className="pkmn-stat-bst">BST {statList.reduce((s, { key }) => s + (displayStats[key] || 0), 0)}</div>
                          </div>
                        )}
                        <div className="saved-pokemon-card-moves">
                          {(p.moves || []).map(m => {
                            const mt = MOVE_TYPES[m] || STATUS_MOVE_TYPES[m] || null;
                            const md = MOVE_DATA[m];
                            const power = md?.power;
                            const cat = md?.cat;
                            const isStatus = !MOVE_TYPES[m];
                            const catIcon = cat === 'physical' ? '⚔' : cat === 'special' ? '◎' : '';
                            const tipParts = [mt ? `${mt}-type` : 'Status', cat || (isStatus ? 'status' : null), power && `${power} BP`, md?.spread && 'spread'].filter(Boolean);
                            return (
                              <span key={m} className="saved-pokemon-move" style={moveStyle(m)} data-tooltip={tipParts.join(' · ')}>
                                <img src={getTypeIconUrl(mt || 'Normal')} alt="" className="move-type-icon" />
                                {m}{power ? <span className="move-power">{catIcon}{power}</span> : isStatus ? <span className="move-power">◈</span> : null}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export { TypeBadge, TYPE_COLORS };
export default TeamBrowser;
